import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Undo2, Layers, Plus, Move } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ToothSvg from "./ToothSvg";
import ToothDetailSheet from "./ToothDetailSheet";
import ChartLegend from "./ChartLegend";
import { rowsFor, styleFor } from "@/lib/dental";

/**
 * The chart surface.
 *
 * Edits are held locally and sent as ONE batch on Save. Clinic wifi drops
 * mid-appointment, and a per-tap round trip would lose findings silently; a
 * pending count makes it obvious there is unsaved work.
 */
export default function Odontogram({ chart, meta, onSave, onSaveLayout, saving = false }) {
  const [dentition, setDentition] = useState("permanent");
  const [pending, setPending] = useState([]);        // findings not yet sent
  const [sheetTooth, setSheetTooth] = useState("");
  const [multi, setMulti] = useState([]);            // multi-select tooth numbers
  const [addOpen, setAddOpen] = useState(false);     // supernumerary picker

  // --- Arrange mode: map teeth to their ACTUAL positions --------------------
  // Real mouths deviate from the textbook chart — transposed teeth, a retained
  // primary molar in the adult arch, a supernumerary between two specific
  // teeth. The layout is per-patient DISPLAY order only; the findings log never
  // changes because a tooth was drawn elsewhere.
  const [arrange, setArrange] = useState(false);
  const [workLayout, setWorkLayout] = useState(null); // editable copy while arranging
  const [picked, setPicked] = useState("");           // tooth in hand (tap or drag)

  const isUpperCode = (t) => /^[1256AB]/.test(t);

  const defs = rowsFor("mixed");
  const persistedLayout = chart?.arch_layout || null;
  const rowSrc = (key, def) => (workLayout?.[key] ?? persistedLayout?.[key] ?? def);
  const archRows = {
    upper: rowSrc("upper", defs.upper),
    lower: rowSrc("lower", defs.lower),
    upper_primary: rowSrc("upper_primary", defs.upperPrimary),
    lower_primary: rowSrc("lower_primary", defs.lowerPrimary),
  };
  const rows = dentition === "primary"
    ? { upper: archRows.upper_primary, lower: archRows.lower_primary }
    : dentition === "mixed"
      ? { upper: archRows.upper, lower: archRows.lower, upperPrimary: archRows.upper_primary, lowerPrimary: archRows.lower_primary }
      : { upper: archRows.upper, lower: archRows.lower };

  // Server chart + local edits, so the grid shows what the dentist just tapped.
  const displayTeeth = useMemo(() => {
    const teeth = JSON.parse(JSON.stringify(chart?.teeth || {}));
    const superseding = meta?.superseding_findings || [];
    for (const f of pending) {
      // Letter-prefixed codes are supernumerary (ISO 10394). Note a naive
      // `code[0] >= "5"` test would call them primary, since "A" > "5".
      const isSuper = /^[A-D]/.test(f.tooth);
      const t = teeth[f.tooth] || {
        dentition: isSuper ? "supernumerary" : (f.tooth[0] >= "5" ? "primary" : "permanent"),
        whole: null,
        surfaces: {},
      };
      const mark = { finding_type: f.finding_type, status: f.status, material: f.material, pending: true };
      if (!f.surfaces?.length) {
        t.whole = mark;
        if (superseding.includes(f.finding_type)) t.surfaces = {};
      } else {
        if (t.whole && superseding.includes(t.whole.finding_type)) t.whole = null;
        f.surfaces.forEach((s) => { t.surfaces[s] = { ...mark }; });
      }
      teeth[f.tooth] = t;
    }
    return teeth;
  }, [chart, pending, meta]);

  const dirtyTeeth = useMemo(() => new Set(pending.map((p) => p.tooth)), [pending]);

  // Supernumerary teeth are only drawn once they exist on this patient —
  // rendering 34 permanently-empty extra boxes would bury the normal arches.
  // (Open Dental never draws them at all, which makes charted work invisible.)
  // Once one has been placed into an arch row via Arrange, it renders inline
  // there instead of in the +U/+L overflow row.
  const placed = new Set([
    ...archRows.upper, ...archRows.lower,
    ...archRows.upper_primary, ...archRows.lower_primary,
  ]);
  const activeSupers = {
    upper: (meta?.supernumerary_upper || []).filter((t) => displayTeeth[t] && !placed.has(t)),
    lower: (meta?.supernumerary_lower || []).filter((t) => displayTeeth[t] && !placed.has(t)),
  };

  const moveTooth = (src, target) => {
    if (!src || src === target) return;
    if (isUpperCode(src) !== isUpperCode(target)) {
      toast.error("A tooth can only be moved within its own arch");
      return;
    }
    setWorkLayout((prev) => {
      const next = {
        upper: [...prev.upper], lower: [...prev.lower],
        upper_primary: [...prev.upper_primary], lower_primary: [...prev.lower_primary],
      };
      const targetRow = Object.keys(next).find((k) => next[k].includes(target));
      if (!targetRow) { toast.error("Drop it onto a tooth in the arch"); return prev; }
      for (const k of Object.keys(next)) next[k] = next[k].filter((t) => t !== src);
      next[targetRow].splice(next[targetRow].indexOf(target), 0, src);
      return next;
    });
  };

  const openTooth = (tooth) => {
    if (arrange) {
      // Tap-tap: pick a tooth up, then tap the tooth it should sit in front of.
      // Reliable with gloves, where a drag is fumbly; dragging also works.
      if (!picked) { setPicked(tooth); return; }
      if (picked === tooth) { setPicked(""); return; }
      moveTooth(picked, tooth);
      setPicked("");
      return;
    }
    if (multi.length) {
      // In multi-select, tapping toggles membership instead of opening.
      setMulti((prev) => prev.includes(tooth) ? prev.filter((t) => t !== tooth) : [...prev, tooth]);
      return;
    }
    setSheetTooth(tooth);
  };

  const beginArrange = () => {
    setMulti([]);
    setPicked("");
    setWorkLayout({
      upper: [...archRows.upper], lower: [...archRows.lower],
      upper_primary: [...archRows.upper_primary], lower_primary: [...archRows.lower_primary],
    });
    setArrange(true);
  };

  const stopArrange = () => { setArrange(false); setWorkLayout(null); setPicked(""); };

  const layoutDirty = arrange && JSON.stringify(workLayout) !== JSON.stringify({
    upper: persistedLayout?.upper ?? defs.upper,
    lower: persistedLayout?.lower ?? defs.lower,
    upper_primary: persistedLayout?.upper_primary ?? defs.upperPrimary,
    lower_primary: persistedLayout?.lower_primary ?? defs.lowerPrimary,
  });

  const saveLayout = async () => {
    try { await onSaveLayout(workLayout); stopArrange(); }
    catch { toast.error("Could not save tooth positions"); }
  };

  const resetLayout = async () => {
    if (!window.confirm("Reset tooth positions to the standard chart?")) return;
    try { await onSaveLayout({ reset: true }); stopArrange(); }
    catch { toast.error("Could not reset tooth positions"); }
  };

  const addPending = (finding) => {
    // A multi-selection applies the same finding to every selected tooth —
    // "these four are missing" is a routine entry, not an edge case.
    const targets = multi.length ? multi : [finding.tooth];
    setPending((prev) => [...prev, ...targets.map((t) => ({ ...finding, tooth: t }))]);
    setMulti([]);
  };

  const undoLast = () => setPending((prev) => prev.slice(0, -1));

  const save = async () => {
    if (!pending.length) return;
    try {
      await onSave(pending);
      setPending([]);
    } catch (e) {
      // Deliberately keep pending on failure so nothing charted is lost.
      toast.error("Could not save — your changes are still here, try again");
    }
  };

  const Row = ({ teeth, label }) => (
    <div className="flex items-center gap-1">
      <span className="w-6 text-[10px] text-slate-400 shrink-0">{label}</span>
      <div className="flex gap-1 flex-1 justify-center flex-wrap">
        {teeth.map((t, i) => (
          <div
            key={t}
            className={(i === teeth.length / 2 ? "ml-3 " : "") + (arrange && picked === t ? "opacity-40" : "")}
            draggable={arrange}
            onDragStart={arrange ? () => setPicked(t) : undefined}
            onDragOver={arrange ? (e) => e.preventDefault() : undefined}
            onDrop={arrange ? (e) => { e.preventDefault(); moveTooth(picked, t); setPicked(""); } : undefined}
          >
            <ToothSvg
              tooth={t}
              entry={displayTeeth[t]}
              selected={multi.includes(t) || (arrange && picked === t)}
              dirty={dirtyTeeth.has(t)}
              onSelect={openTooth}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Card className="p-4" data-testid="odontogram">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <select
            value={dentition}
            onChange={(e) => { setDentition(e.target.value); setMulti([]); }}
            className="h-9 rounded-md border border-slate-300 px-2 text-sm"
            data-testid="dentition-select"
          >
            <option value="permanent">Permanent</option>
            <option value="primary">Primary</option>
            <option value="mixed">Mixed</option>
          </select>
          {!arrange ? (
            <>
              <Button
                size="sm"
                variant={multi.length ? "default" : "outline"}
                onClick={() => setMulti(multi.length ? [] : ["__arm__"])}
                title="Select several teeth, then apply one finding to all"
                data-testid="btn-multi-select"
              >
                <Layers size={14} className="mr-1" />
                {multi.length ? `${multi.filter((t) => t !== "__arm__").length} selected` : "Multi-select"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setAddOpen(true)}
                title="Record a supernumerary (extra) tooth"
                data-testid="btn-add-supernumerary"
              >
                <Plus size={14} className="mr-1" /> Extra tooth
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={beginArrange}
                title="Move teeth to match their actual positions in this mouth"
                data-testid="btn-arrange"
              >
                <Move size={14} className="mr-1" /> Arrange
              </Button>
            </>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          {arrange ? (
            <>
              {persistedLayout ? (
                <Button size="sm" variant="ghost" onClick={resetLayout} data-testid="btn-reset-layout">
                  <Undo2 size={14} className="mr-1" /> Standard order
                </Button>
              ) : null}
              <Button size="sm" variant="outline" onClick={stopArrange} data-testid="btn-cancel-arrange">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveLayout}
                disabled={!layoutDirty}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="btn-save-layout"
              >
                <Save size={14} className="mr-1" /> Save positions
              </Button>
            </>
          ) : (
            <>
              {pending.length ? (
                <>
                  <span
                    className="text-xs font-semibold px-2 py-1 rounded-full bg-amber-100 text-amber-800"
                    data-testid="pending-count"
                  >
                    {pending.length} unsaved
                  </span>
                  <Button size="sm" variant="outline" onClick={undoLast}>
                    <Undo2 size={14} className="mr-1" /> Undo
                  </Button>
                </>
              ) : null}
              <Button
                size="sm"
                onClick={save}
                disabled={!pending.length || saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-save-chart"
              >
                <Save size={14} className="mr-1" />
                {saving ? "Saving…" : "Save chart"}
              </Button>
            </>
          )}
        </div>
      </div>

      {arrange ? (
        <div className="mb-3 text-xs bg-indigo-50 border border-indigo-200 rounded px-3 py-2 text-indigo-900" data-testid="arrange-hint">
          {picked ? (
            <>Moving <b>{picked}</b> — tap the tooth it should sit in front of, or tap it again to cancel.</>
          ) : (
            <>Tap (or drag) a tooth, then tap where it goes. Extra teeth can be pulled from the +U/+L rows
            into the arch. Switch to <b>Mixed</b> to move a baby tooth into the adult row.</>
          )}
        </div>
      ) : null}

      {multi.length ? (
        <div className="mb-3 text-xs bg-blue-50 border border-blue-200 rounded px-3 py-2 text-blue-900">
          Tap the teeth you want, then tap <b>Apply to selected</b>.
          {multi.filter((t) => t !== "__arm__").length ? (
            <Button
              size="sm"
              className="ml-3 h-7 bg-blue-600 hover:bg-blue-700"
              onClick={() => setSheetTooth(multi.find((t) => t !== "__arm__"))}
              data-testid="btn-apply-multi"
            >
              Apply to selected
            </Button>
          ) : null}
        </div>
      ) : null}

      <div className="space-y-4 overflow-x-auto">
        {activeSupers.upper.length ? <Row teeth={activeSupers.upper} label="+U" /> : null}
        <Row teeth={rows.upper} label="U" />
        {dentition === "mixed" && rows.upperPrimary ? <Row teeth={rows.upperPrimary} label="u" /> : null}
        <div className="border-t border-dashed border-slate-300" />
        {dentition === "mixed" && rows.lowerPrimary ? <Row teeth={rows.lowerPrimary} label="l" /> : null}
        <Row teeth={rows.lower} label="L" />
        {activeSupers.lower.length ? <Row teeth={activeSupers.lower} label="+L" /> : null}
      </div>

      <ChartLegend />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="supernumerary-picker">
          <DialogHeader>
            <DialogTitle>Add an extra (supernumerary) tooth</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">
            Pick where the extra tooth sits. Codes follow {meta?.supernumerary_notation || "ISO 10394"},
            which designates a supernumerary by the normal tooth it sits at or just behind.
          </p>
          <div className="max-h-[55vh] overflow-auto divide-y divide-slate-100">
            {(meta?.supernumerary || []).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => { setAddOpen(false); setSheetTooth(t); }}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 flex items-center gap-3"
                data-testid={`super-pick-${t}`}
              >
                <span className="mono text-xs font-bold text-blue-700 w-8 shrink-0">{t}</span>
                <span className="text-sm text-slate-700 truncate">
                  {meta?.supernumerary_labels?.[t] || t}
                </span>
                {displayTeeth[t] ? (
                  <span className="ml-auto text-[10px] uppercase tracking-wider text-emerald-700 shrink-0">
                    charted
                  </span>
                ) : null}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <ToothDetailSheet
        open={!!sheetTooth}
        onOpenChange={(o) => !o && setSheetTooth("")}
        tooth={sheetTooth}
        entry={displayTeeth[sheetTooth]}
        meta={meta}
        onApply={addPending}
      />
    </Card>
  );
}
